import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, orderBy, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { getCurrentUser } from '../services/authService';
import { useWindowSize, BP } from '../hooks/useWindowSize';

function OfferLettersPage() {
  const navigate = useNavigate();
  const { width } = useWindowSize();
  const isMobile = width < BP.mobile;
  const isTablet = width >= BP.mobile && width < BP.tablet;
  const user = getCurrentUser();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (!user) return;
    const q = query(
      collection(db, 'offer_letters'),
      where('employerId', '==', user.uid),
      orderBy('sentAt', 'desc')
    );
    const unsub = onSnapshot(q, async (snap) => {
      const offersData = await Promise.all(snap.docs.map(async (d) => {
        const data = { id: d.id, ...d.data() };
        // Fetch worker name if not on doc — HC-INFRA-008C, Prompt #422:
        // reads the public projection (publicWorkerProfiles/{uid}, Prompt
        // #419), not the raw users/{uid} doc. displayName is the only
        // name field this projection ever carries (no email). A real,
        // small fix made while here: the pre-migration code only set an
        // 'Unknown' fallback when the doc existed but displayName was
        // falsy — a genuinely-missing/failed lookup left data.workerName
        // unset, silently falling through to this page's own generic '—'
        // render fallback instead of a real placeholder. Now explicitly
        // falls back to 'Unknown' in both cases.
        if (!data.workerName && data.workerId) {
          try {
            const workerDoc = await getDoc(doc(db, 'publicWorkerProfiles', data.workerId));
            data.workerName = workerDoc.exists() ? (workerDoc.data().displayName || 'Unknown') : 'Unknown';
          } catch (_) {
            data.workerName = 'Unknown';
          }
        }
        return data;
      }));
      setOffers(offersData);
      setLoading(false);
    });
    return () => unsub();
  }, [user]);

  const formatDate = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getStatusBadge = (offer) => {
    if (offer.status === 'draft') return { label: 'Draft', bg: '#f3f4f6', color: '#374151' };
    if (offer.workerResponse === 'accepted') return { label: 'Accepted', bg: '#d1fae5', color: '#065f46' };
    if (offer.workerResponse === 'declined') return { label: 'Declined', bg: '#fee2e2', color: '#991b1b' };
    if (offer.status === 'sent') return { label: 'Pending', bg: '#fef3c7', color: '#92400e' };
    return { label: offer.status, bg: '#f3f4f6', color: '#374151' };
  };

  const filtered = offers.filter(o => {
    if (filter === 'all') return true;
    if (filter === 'pending') return o.status === 'sent' && !o.workerResponse;
    if (filter === 'accepted') return o.workerResponse === 'accepted';
    if (filter === 'declined') return o.workerResponse === 'declined';
    if (filter === 'draft') return o.status === 'draft';
    return true;
  });

  const tabs = [
    { key: 'all', label: 'All' },
    { key: 'pending', label: 'Pending' },
    { key: 'accepted', label: 'Accepted' },
    { key: 'declined', label: 'Declined' },
    { key: 'draft', label: 'Draft' },
  ];

  return (
    <>
      {/* Best viewed on desktop notice — tablet only; mobile gets card layout */}
      {isTablet && (
        <div style={{
          backgroundColor: '#fef3c7',
          border: '1px solid #f59e0b',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          fontSize: '14px',
          color: '#92400e',
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          This page is best viewed on a desktop for full functionality.
        </div>
      )}
      <div style={{ backgroundColor: '#F8FAFC', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        {/* Filter tabs */}
        <div style={{ display: 'flex', flexWrap: 'nowrap', overflowX: 'auto', WebkitOverflowScrolling: 'touch', borderBottom: '1px solid #e5e7eb', padding: '0 1rem' }}>
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              style={{
                padding: isMobile ? '0.75rem 1rem' : '1rem 1.25rem',
                border: 'none',
                borderBottom: filter === tab.key ? '3px solid #E8590C' : '3px solid transparent',
                backgroundColor: 'transparent',
                color: filter === tab.key ? '#E8590C' : '#6b7280',
                fontWeight: filter === tab.key ? '600' : '400',
                fontSize: '14px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: '#6b7280' }}>Loading...</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center' }}>
            <div style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
            </div>
            <p style={{ color: '#6b7280', margin: 0 }}>No offer letters found.</p>
          </div>
        ) : isMobile ? (
          /* Mobile: card layout */
          <div style={{ padding: '12px' }}>
            {filtered.map(offer => {
              const badge = getStatusBadge(offer);
              return (
                <div key={offer.id} style={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', marginBottom: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px', gap: '8px' }}>
                    <div style={{ fontWeight: '600', fontSize: '14px', color: '#0F1E2E', flex: 1 }}>{offer.workerName || '—'}</div>
                    <span style={{ padding: '0.2rem 0.6rem', borderRadius: '12px', fontSize: '12px', fontWeight: '600', backgroundColor: badge.bg, color: badge.color, flexShrink: 0 }}>{badge.label}</span>
                  </div>
                  <div style={{ fontSize: '13px', color: '#374151', marginBottom: '4px' }}>{offer.jobTitle || '—'}</div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>{offer.company || ''}</div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {offer.pdfUrl && (
                      <button
                        onClick={() => window.open(offer.pdfUrl, '_blank')}
                        style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', padding: '8px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer', backgroundColor: '#0F1E2E', color: 'white' }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                        View PDF
                      </button>
                    )}
                    {offer.conversationId && (
                      <button
                        onClick={() => navigate(`/chat/${offer.conversationId}`)}
                        style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', padding: '8px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer', backgroundColor: '#1a3048', color: 'white' }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                        Go to Chat
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* Desktop/tablet: grid table */
          <div>
            {/* Header */}
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr 1fr 0.8fr', backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb', padding: '0.75rem 1rem', gap: '0.5rem' }}>
              {['Worker', 'Job', 'Status', 'Sent Date', 'Actions'].map(h => (
                <div key={h} style={{ fontWeight: '600', fontSize: '13px', color: '#374151' }}>{h}</div>
              ))}
            </div>
            {/* Rows */}
            {filtered.map(offer => {
              const badge = getStatusBadge(offer);
              return (
                <div
                  key={offer.id}
                  style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr 1fr 0.8fr', borderBottom: '1px solid #f3f4f6', padding: '1rem', gap: '0.5rem', alignItems: 'center' }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f9fafb'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <div style={{ fontSize: '14px', fontWeight: '500', color: '#0F1E2E' }}>{offer.workerName || '—'}</div>
                  <div>
                    <div style={{ fontSize: '14px', color: '#374151' }}>{offer.jobTitle || '—'}</div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>{offer.company || ''}</div>
                  </div>
                  <div>
                    <span style={{ padding: '0.2rem 0.6rem', borderRadius: '12px', fontSize: '12px', fontWeight: '600', backgroundColor: badge.bg, color: badge.color }}>
                      {badge.label}
                    </span>
                  </div>
                  <div style={{ fontSize: '13px', color: '#6b7280' }}>{formatDate(offer.sentAt)}</div>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    {offer.pdfUrl && (
                      <div
                        onClick={() => window.open(offer.pdfUrl, '_blank')}
                        title="View PDF"
                        style={{ cursor: 'pointer', color: '#0f766e' }}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                      </div>
                    )}
                    {offer.conversationId && (
                      <div
                        onClick={() => navigate(`/chat/${offer.conversationId}`)}
                        title="Open Chat"
                        style={{ cursor: 'pointer', color: '#8b5cf6' }}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}

export default OfferLettersPage;
